import { Menu, MenuItem } from "@mui/material";
import { MuiBox, MuiTypography } from "components/atoms";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { openAdminModal } from "store/slices/common/adminModal.slice";
import { Button } from "components/ui";
import { EllipsisVertical, Pencil, Trash } from "lucide-react";
import DeleteIconSVGModel from "assets/images/deleteIconSVGModel.svg";
import ModalEditEmployment from "components/molecules/DataManagement/ModalEditEmployment";
import { DeleteEmploymentAction } from "store/middlewares/dataManagement/employment";
import checkAccess from "utils/checkAccess";

const Employment = ({ row, onActionComplete }) => {
  const dispatch = useDispatch();

  const handleEdit = () => {
    dispatch(
      openAdminModal({
        title: "Edit Employment",
        content: <ModalEditEmployment row={row} />,
      })
    );
    // After opening edit modal, refresh data and clear search
    if (onActionComplete) {
      console.log("Edit: Calling onActionComplete");
      onActionComplete();
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: `Are you sure you want to delete this employment?`,
      imageUrl: DeleteIconSVGModel,
      imageWidth: 100,
      imageHeight: 100,
      showCancelButton: true,
      confirmButtonText: "Confirm",
      customClass: {
        popup: "custom-swal-popup",
        title: "Swal-title-class",
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        console.log("Delete confirmed for ID:", row.id);
        
        // Try multiple approaches to ensure refresh happens
        
        // Approach 1: Call onActionComplete immediately
        console.log("Delete: Calling onActionComplete immediately");
        if (onActionComplete) {
          onActionComplete();
        }
        
        // Approach 2: Also try with setTimeout
        setTimeout(() => {
          console.log("Delete: Calling onActionComplete with setTimeout");
          if (onActionComplete) {
            onActionComplete();
          }
        }, 500);
        
        // Dispatch delete action
        dispatch(
          DeleteEmploymentAction({ 
            id: row.id,
            onSuccess: () => {
              console.log("Delete: onSuccess callback executed");
              // Approach 3: Call in onSuccess
              if (onActionComplete) {
                console.log("Delete: Calling onActionComplete in onSuccess");
                onActionComplete();
              }
              Swal.fire({
                title: "Deleted!",
                text: "Employment has been deleted successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false
              });
            }
          })
        );
      }
    });
  };

  const ActionMenuArray = [
    checkAccess("update_employment") && {
      label: "Edit",
      icon: <Pencil size={16} color="#475569" />,
      onClick: handleEdit,
    },
    row.total_count <= 0 && 
    checkAccess("delete_employment") && {
      label: "Delete",
      icon: <Trash size={16} color="#475569" />,
      onClick: handleDelete,
    },
  ].filter(Boolean);

  const [actionMenu, setActionMenu] = useState(null);
  const handleActionMenuOpen = (event) => {
    setActionMenu(event.currentTarget);
  };
  const handleActionMenuClose = () => {
    setActionMenu(null);
  };

  return (
    <MuiBox className="flex h-full items-center justify-center gap-3">
      <Button
        className="size-7"
        mode="icon"
        variant="ghost"
        onClick={handleActionMenuOpen}
      >
        <EllipsisVertical />
      </Button>
      <Menu
        anchorEl={actionMenu}
        open={Boolean(actionMenu)}
        onClose={() => handleActionMenuClose(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          " & .MuiMenu-paper": {
            mt: "10px",
            background: "#fff",
            boxShadow:
              "0 4px 6px -1px #e2e8f0, 0 2px 4px -2px #e2e8f0 !important",
          },
        }}
      >
        {ActionMenuArray.map((option) => (
          <MenuItem
            key={option.label}
            onClick={() => {
              handleActionMenuClose();
              option.onClick();
            }}
          >
            <MuiBox sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
              <MuiBox sx={{ width: "fit-content" }}>{option.icon}</MuiBox>
              <MuiTypography
                sx={{ fontWeight: "500", fontSize: "13px", color: "#475569" }}
              >
                {option.label}
              </MuiTypography>
            </MuiBox>
          </MenuItem>
        ))}
      </Menu>
    </MuiBox>
  );
};

export default Employment;